import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, effect, inject, input } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { DxMenuModule, DxScrollViewModule } from 'devextreme-angular';
import { Subscription } from 'rxjs';
import { hasValue, stringEquals } from '@my-aon/utils';
import { AuthenticationService } from '@my-aon/auth';
import { Navigation } from '../../lib/models';
import { TranslatePipe } from '@ngx-translate/core';

//app

export interface MenuItem {
	icon?: string;
	iconType?: 'mat' | 'fa' | 'bi' | 'txt';
	path?: string;
	parentPath?: string;
	routerLink?: string | string[];
	template?: 'itemTemplateHome' | 'itemTemplateParent' | 'itemTemplateChild';
	selected?: boolean;
	title?: string;
	label?: string;

	items?: MenuItem[];
}

@Component({
    selector: 'app-sub-navbar',
    templateUrl: './sub-navbar.component.html',
    styleUrls: ['./sub-navbar.component.scss'],
    imports: [
        CommonModule,
        DxMenuModule,
        DxScrollViewModule,
        RouterModule,
        TranslatePipe,
    ],
    host: {
        '[id]': 'id',
    }
})
export class SubNavbarComponent implements OnInit, OnDestroy {

	id = Date.now().toString();

	readonly router: Router = inject(Router);
	readonly authService: AuthenticationService = inject(AuthenticationService);

	$showHome = input<boolean>(true, { alias: 'showHome' });

	$prefix = input.required<string>({ alias: 'prefix' });
	prefix!: string;

	$navigation = input.required<Navigation[]>({ alias: 'navigation' });

	dataSource: MenuItem[] = [];

	selectedRoute = '';
	selectedRouteParts: string[] = [];

	private subscriptions: Subscription[] = [];
	set subscription(val: Subscription) {
		this.subscriptions.push(val);
	}

	constructor() {
		effect(() => {
			let prefix = this.prefix = this.$prefix();
			if (prefix) this.buildMenu();
		})

		effect(() => {
			let navigation = this.$navigation();
			if (navigation) this.buildMenu();
		})
	}

	ngOnInit(): void {

		const URL = this.router.url;

		this.selectedRoute = URL.split('?')[0];
		this.selectedRouteParts = this.selectedRoute.split('/').filter(x => !!x);

		this.subscription = this.router.events.subscribe(val => {
			if (val instanceof NavigationEnd) {
				this.selectedRoute = val.urlAfterRedirects.split('?')[0];
				this.selectedRouteParts = this.selectedRoute.split('/').filter(x => !!x);

				this.buildMenu();
			}

		});

	}

	ngOnDestroy(): void {
		this.subscriptions?.forEach(x => x.unsubscribe());
	}

	isActiveRoute(path?: string): boolean {
		if (!path || !hasValue(path)) return false;

		return stringEquals(path, this.selectedRoute) || this.selectedRoute.startsWith(path) || this.selectedRoute.endsWith(path);
	}

	isAnyChildItemActive(items: MenuItem[] | null): boolean {
		return !!items && items?.some(x => this.isItemActive(x));
	}

	isItemActive(item: MenuItem): boolean {
		return item.selected || this.isActiveRoute([item.parentPath, item.path].filter(x => !!x).join('/'));
	}

	buildMenu = () => {
		const iconType = (item: Navigation): 'mat' | 'fa' | 'bi' | 'txt' => {
			if (hasValue(item?.icon?.fa)) return 'fa';
			if (hasValue(item?.icon?.bi)) return 'bi';
			if (hasValue(item?.icon?.mat)) return 'mat';
			return 'txt';
		}

		const filter = (item: Navigation) =>
			this.authService.hasAllPermissions(...(item.requiredPermissions ?? [])) &&
			this.authService.hasAnyPermission(...(item.requiredPermissionsAny ?? []));
		const map = (item: Navigation, parentPath?: string): MenuItem => {

			let items: MenuItem[] | null = item.items && hasValue(item.items) ? item?.items?.filter(filter)?.map(x => map(x, [parentPath, item.path].filter(x => !!x).join('/'))) : null;
			let selected = this.isAnyChildItemActive(items);

			return {
				...item,
				items,
				icon: item.icon && (item.icon?.fa ?? item.icon?.bi ?? item.icon?.mat ?? item.icon?.txt),
				iconType: iconType(item),
				template: hasValue(items) ? 'itemTemplateParent' : 'itemTemplateChild',
				path: item.path,
				parentPath,
				selected
			} as MenuItem
		}

		this.dataSource = [
			this.$showHome() ? { icon: 'fa-solid fa-house fa-fw', iconType: 'fa', routerLink: ['.'], template: 'itemTemplateHome' } as MenuItem : null,
			...this.$navigation()
				?.filter(filter)
				?.map(x => map(x, this.prefix))
		].filter(x => !!x) as MenuItem[];

	}

	routerLink = ({ path, parentPath, routerLink }: MenuItem): string | string[] => {
		return routerLink || ('/' + [parentPath, path].filter(x => !!x).join('/')).replace(/[\/]{2,}/gi, '/')
	}

}
